import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import OpenInNew from "@mui/icons-material/OpenInNew";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  Alert,
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import { format } from "date-fns";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { Link } from "react-router-dom";
import {
  GetSubmissionQuery,
  ReviewerStatus,
  SubmissionStatus,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { EmbeddedDocumentSigning } from "./EmbeddedSignature/EmbeddedDocumentSigning";
import { SubmissionPaymentDisplay } from "./SubmissionPaymentDisplay";
import { getStaffAssigneeName } from "./helpers/displayHelpers";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { getReviewersSubmissionDocumentReview } from "hooks/submission/submissionReviewHelpers";
import { SubmissionDocumentPreview } from "./SubmissionDocumentPreview";

type Submission = NonNullable<GetSubmissionQuery["submission"]>;

interface DocumentDisplay {
  id: string;
  name: string;
  documentType: string;
  url?: string;
}

interface SubmissionDisplayStaticProps {
  submission: Submission;
  reviewState: SubmissionReviewState;
  facilityDisplay: string;
  reviewers: Submission["reviewers"];
  signer: Submission["reviewers"][number] | undefined;
  submissionDocuments: DocumentDisplay[];
  attachmentDocuments: DocumentDisplay[];
  previewDocumentUrl: string | null;
  signingCompleted: boolean;
  signingDialogOpen: boolean;
  signingUrlData?: {
    getEmbeddedDocumentSigningUrl?: string | null | undefined;
  };
  tenantId: string;
  onDocumentPreview: (document: { id: string }) => void;
  onSigningDialogClose: () => void;
  onSigningDialogOpen: () => void;
  onSigningCompleted: () => void;
}

// Unified component for displaying reviewers and signers
const ReviewerSignerDisplay = ({
  person,
  isCompleted = false,
  approved = false,
}: {
  person: NonNullable<GetSubmissionQuery["submission"]>["reviewers"][number];
  isCompleted?: boolean;
  approved?: boolean;
}) => {
  const user = person.user || person;
  const personData = user.person;
  const displayName = personData
    ? `${personData.first || ""} ${personData.last || ""}`.trim()
    : "";
  const finalDisplay = displayName || user.email || "Unknown";
  const email = user.email;

  let icon = null;
  if (isCompleted && approved) {
    icon = <CheckIcon sx={{ color: "success.main", fontSize: 20 }} />;
  } else if (isCompleted && !approved) {
    icon = <CloseIcon sx={{ color: "error.main", fontSize: 20 }} />;
  } else {
    icon = <AccessTimeIcon sx={{ color: "warning.main", fontSize: 20 }} />;
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {icon}
      <Typography variant="body1">
        {displayName && email ? `${displayName}, ${email}` : finalDisplay}
      </Typography>
    </Box>
  );
};

export function SubmissionDisplayStatic({
  submission,
  reviewState,
  facilityDisplay,
  reviewers,
  signer,
  submissionDocuments,
  attachmentDocuments,
  previewDocumentUrl,
  signingCompleted,
  signingDialogOpen,
  signingUrlData,
  tenantId,
  onDocumentPreview,
  onSigningDialogClose,
  onSigningDialogOpen,
  onSigningCompleted,
}: SubmissionDisplayStaticProps) {
  const { isStaff, user } = useCurrentUser();
  const theme = useTheme();
  const submissionStatus = submission.status;

  // Check if current user is a signer on this submission
  const isSigner = reviewers.some(
    (reviewer) => reviewer.isSigner && reviewer.user.id === user?.id
  );

  const formatDueDate = (date?: string | null) => {
    if (!date) {
      return "";
    }
    try {
      return format(new Date(date), "MM/dd/yyyy");
    } catch {
      return "None assigned";
    }
  };

  return (
    <Grid container spacing={4}>
      {/* Don't show this to staff because it's telling non staff users Encamp staff are working on it */}
      {!isStaff &&
        [
          SubmissionStatus.NotStarted,
          SubmissionStatus.InProgress,
          SubmissionStatus.EncampInitialReview,
        ].includes(submissionStatus) && (
          <Grid size={12}>
            <Alert
              severity="warning"
              icon={<HourglassTopIcon />}
              sx={{ borderRadius: "4px" }}
            >
              <Typography fontWeight="bold" marginBottom={0.5}>
                Submission not ready for review
              </Typography>
              <Typography variant="body2">
                This submission is being worked on by Encamp Staff and the
                reviewer(s) and signer will be notified when it is ready for
                review.
              </Typography>
            </Alert>
          </Grid>
        )}

      {reviewState ===
        SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS && (
        <Grid size={12}>
          <Alert
            severity="warning"
            icon={<HourglassTopIcon />}
            sx={{ borderRadius: "4px" }}
          >
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission not ready for signature
            </Typography>
            <Typography variant="body2">
              This submission cannot be signed until all preceding reviews are
              complete. We will notify the signatory via email once it is ready
              signing.
            </Typography>
          </Alert>
        </Grid>
      )}

      {reviewState === SubmissionReviewState.REVIEWER_WAITING_FOR_TURN && (
        <Grid size={12}>
          <Alert
            severity="warning"
            icon={<HourglassTopIcon />}
            sx={{ borderRadius: "4px" }}
          >
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission is not ready for review
            </Typography>
            <Typography variant="body2">
              The submission cannot be reviewed until all preceding reviews are
              complete. We will notify you via email once it is ready for
              review.
            </Typography>
          </Alert>
        </Grid>
      )}
      {submission.status === SubmissionStatus.ProcessingSubmission && (
        <Grid size={12}>
          <Alert
            severity="warning"
            icon={<HourglassTopIcon />}
            sx={{ borderRadius: "4px" }}
          >
            <Typography fontWeight="bold" marginBottom={0.5}>
              Submission is being processed by Encamp Staff
            </Typography>
            <Typography variant="body2">
              Submission will be updated when completed
            </Typography>
          </Alert>
        </Grid>
      )}
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Title
            </Typography>
            <Typography variant="body1">{submission.title}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Facility
            </Typography>
            <Typography variant="body1">{facilityDisplay}</Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid size={6}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Due Date
                </Typography>
                <Typography variant="body1">
                  {formatDueDate(submission.dueDate)}
                </Typography>
              </Box>
            </Grid>
            <Grid size={6}>
              <Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 0.5 }}
                >
                  Recipient
                </Typography>
                <Typography variant="body1">
                  {prettyPrintEnumValue(submission.recipient) || ""}
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Encamp Assignee
            </Typography>
            <Typography
              variant="body1"
              color={
                submission.staffAssignee ? "text.primary" : "text.secondary"
              }
            >
              {getStaffAssigneeName(submission.staffAssignee)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Reviewers
            </Typography>
            {reviewers.length > 0 ? (
              <Stack spacing={0.5}>
                {reviewers.map((reviewer) => {
                  const mostRecentSDR = getReviewersSubmissionDocumentReview(
                    submission,
                    reviewer.user.id
                  );
                  return (
                    <ReviewerSignerDisplay
                      key={reviewer.id}
                      person={reviewer}
                      isCompleted={mostRecentSDR?.completedAt !== null}
                      approved={
                        mostRecentSDR?.reviewerStatus ===
                        ReviewerStatus.Approved
                      }
                    />
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No reviewers assigned
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Signer
            </Typography>
            {signer ? (
              (() => {
                const signerSubmissionDocumentReview =
                  getReviewersSubmissionDocumentReview(
                    submission,
                    signer.user.id
                  );
                return (
                  <ReviewerSignerDisplay
                    person={signer}
                    isCompleted={
                      signerSubmissionDocumentReview?.completedAt !== null
                    }
                    approved={
                      signerSubmissionDocumentReview?.reviewerStatus ===
                      ReviewerStatus.Approved
                    }
                  />
                );
              })()
            ) : (
              <Typography variant="body1" color="text.secondary">
                No signer assigned
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Notes
            </Typography>
            {submission.notes ? (
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {submission.notes}
              </Typography>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No notes
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Submission Document
            </Typography>
            {submissionDocuments.length > 0 ? (
              <Stack spacing={1}>
                {submissionDocuments.map((document) => {
                  return (
                    <Box
                      key={document.id}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Typography variant="body1">{document.name}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => onDocumentPreview({ id: document.id })}
                        sx={{ color: theme.palette.grey[400] }}
                        aria-label="Preview document"
                      >
                        <PreviewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        component={Link}
                        to={`/o/${tenantId}/documents/${document.id}`}
                        target="_blank"
                        rel="noopener"
                        size="small"
                        sx={{
                          color: theme.palette.action.active,
                          "&:visited": { color: theme.palette.action.active },
                        }}
                        aria-label={`View ${document.name} in documents`}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No submission documents
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Additional Attachment(s)
            </Typography>
            {attachmentDocuments.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File Name</TableCell>
                      <TableCell>Document Type</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attachmentDocuments.map((document) => {
                      const documentType =
                        document?.documentType || "Unknown Type";

                      return (
                        <TableRow key={document.id}>
                          <TableCell>{document.name}</TableCell>
                          <TableCell>{documentType}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onDocumentPreview({ id: document.id })
                                }
                                sx={{ color: theme.palette.grey[400] }}
                                aria-label="Preview document"
                              >
                                <PreviewIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                component={Link}
                                to={`/o/${tenantId}/documents/${document.id}`}
                                target="_blank"
                                rel="noopener"
                                size="small"
                                sx={{
                                  color: theme.palette.action.active,
                                  "&:visited": {
                                    color: theme.palette.action.active,
                                  },
                                }}
                                aria-label={`View ${document.name} in documents`}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No additional attachments
              </Typography>
            )}
          </Box>
          <SubmissionPaymentDisplay
            submissionPayments={submission.payments || []}
          />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        {previewDocumentUrl ? (
          <SubmissionDocumentPreview
            previewDocumentUrl={previewDocumentUrl}
            reviewState={reviewState}
            signingCompleted={signingCompleted}
            signatureType={submission.signatureType}
            isSigner={isSigner}
            onSigningDialogOpen={onSigningDialogOpen}
          />
        ) : (
          <Paper
            sx={{
              p: 3,
              height: "100%",
              minHeight: 600,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
              backgroundColor: theme.palette.background.paper,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              align="center"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                lineHeight: 1.5,
              }}
            >
              {submissionDocuments.length > 0 || attachmentDocuments.length > 0
                ? "Click on a document chip above to preview"
                : "No documents available for preview"}
            </Typography>
          </Paper>
        )}
        {!!signingUrlData?.getEmbeddedDocumentSigningUrl && (
          <EmbeddedDocumentSigning
            open={signingDialogOpen}
            onSigned={onSigningCompleted}
            onClose={onSigningDialogClose}
            signingUrl={signingUrlData.getEmbeddedDocumentSigningUrl}
          />
        )}
      </Grid>
    </Grid>
  );
}
